import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { environment } from '../app/app.environment';
import { IndustryService } from './industry.service';

// API response for denomination status
export interface DenominationStatusApiResponse {
  status: number;
  message: string;
  data: {
    hasCashInPunched: boolean;
    hasCashOutPunched: boolean;
  };
}

// Extended punch status with shift times (shift times come from login response)
export interface PunchStatus {
  hasCashInPunched: boolean;
  hasCashOutPunched: boolean;
  shiftStartTime: string;  // e.g., "09:00" - from countersession.start_time
  shiftEndTime: string;    // e.g., "18:00" - from countersession.end_time
}

export interface PunchStatusResponse {
  status: number;
  message: string;
  data: PunchStatus;
}

export interface CashDenomination {
  id?: number;
  warehouse_id?: number;
  date?: string;
  amount: number;
  quantity: number;
  total: number;
  createdBy?: number;
  updatedBy?: number | null;
  type?: number; // 0 = Day In, 1 = Day Out
  createdAt?: string;
  updatedAt?: string;
  dayInVerify?: 'Checked' | 'Unchecked';
  dayCloseVerify?: 'Checked' | 'Not Checked';
}

export interface CashDenominationResponse {
  status: number;
  message: string;
  data: CashDenomination[];
}

export interface SaleDataResponse {
  status: number;
  message: string;
  data: SaleData;
}
export interface SaleData{
  //createdAt: string;
  cardamount: string;
  upiamount: string;
  cashamount: string;
  redeemamount: string;
}

@Injectable({
  providedIn: 'root'
})
export class CashDenominationService {
  apiUrl = environment.apiURL + 'v1/cash/denomination';
  saleapiUrl = environment.apiURL + 'v1/sale/';

  constructor(private http: HttpClient, private industryService: IndustryService) {}

  // Day In methods (type = 0)
  getDayInCashDenominations(): Observable<CashDenominationResponse> {
    return this.http.get<CashDenominationResponse>(`${this.apiUrl}?type=0`);
  }

  createDayInCashDenomination(denomination: CashDenomination): Observable<CashDenomination> {
    const payload = { ...denomination, type: 0 }; // Day In = type 0
    return this.http.post<CashDenomination>(this.apiUrl, payload);
  }

  // Day Out methods (type = 1)
  getDayOutCashDenominations(): Observable<CashDenominationResponse> {
    return this.http.get<CashDenominationResponse>(`${this.apiUrl}?type=1`);
  }

  createDayOutCashDenomination(denomination: CashDenomination): Observable<CashDenomination> {
    const payload = { ...denomination, type: 1 }; // Day Out = type 1
    return this.http.post<CashDenomination>(this.apiUrl, payload);
  }

 
  createBatchCashDenominationWithType(data: { amount: number; quantity: number }[], type: number): Observable<any> {
    const payload = {
      type: type,
      denominations: data
    };
    return this.http.post<any>(this.apiUrl, payload);
  }

  //payment wise today sum report
  getDayPaymentwiseCount(): Observable<SaleDataResponse> {
    return this.http.get<SaleDataResponse>(`${this.saleapiUrl}daywisesale?date=2025-07-06`);
  }

  storeShiftTimes(startTime: string, endTime: string): void {
    localStorage.setItem('shiftStartTime', startTime);
    localStorage.setItem('shiftEndTime', endTime);
  }

  getShiftStartTime(): string {
    return localStorage.getItem('shiftStartTime') || '09:00';
  }

  getShiftEndTime(): string {
    return localStorage.getItem('shiftEndTime') || '18:00';
  }

  clearShiftTimes(): void {
    localStorage.removeItem('shiftStartTime');
    localStorage.removeItem('shiftEndTime');
  }

  getPunchStatus(): Observable<PunchStatusResponse> {
    const shiftStartTime = this.getShiftStartTime();
    const shiftEndTime = this.getShiftEndTime();

    // Denomination turned off for this industry (feature-flags.ts): skip the API and report
    // cash in/out as done, so the Opening/Closing Cash prompts never open.
    if (!this.industryService.isDenominationEnabled()) {
      return of({
        status: 200,
        message: 'Denomination disabled for this industry',
        data: { hasCashInPunched: true, hasCashOutPunched: true, shiftStartTime, shiftEndTime },
      });
    }

    return new Observable<PunchStatusResponse>(observer => {
      this.http.get<DenominationStatusApiResponse>(`${environment.apiURL}v1/cash/denaminationstatus`).subscribe({
        next: (response) => {
          const punchStatusResponse: PunchStatusResponse = {
            status: response.status,
            message: response.message,
            data: {
              hasCashInPunched: response.data.hasCashInPunched,
              hasCashOutPunched: response.data.hasCashOutPunched,
              shiftStartTime: shiftStartTime,
              shiftEndTime: shiftEndTime
            }
          };
          observer.next(punchStatusResponse);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

 
  isAfterShiftStart(shiftStartTime: string): boolean {
    const now = new Date();
    const [hours, minutes] = shiftStartTime.split(':').map(Number);
    const shiftStart = new Date();
    shiftStart.setHours(hours, minutes, 0, 0);
    return now >= shiftStart;
  }

  /**
   * Check if current time is after shift end time
   */
  isAfterShiftEnd(shiftEndTime: string): boolean {
    const now = new Date();
    const [hours, minutes] = shiftEndTime.split(':').map(Number);
    const shiftEnd = new Date();
    shiftEnd.setHours(hours, minutes, 0, 0);
    return now > shiftEnd;
  }

  isWithinShiftHours(): boolean {
    const startTime = this.getShiftStartTime();
    const endTime = this.getShiftEndTime();
    return this.isAfterShiftStart(startTime) && !this.isAfterShiftEnd(endTime);
  }
}
